import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { romaneioService, Romaneio, ProdutoRomaneio } from '../../services/romaneio';
import { produtoService, Produto } from '../../services/produto';
import { funcionarioService, Funcionario } from '../../services/funcionario';
import { pracaService, Praca } from '../../services/praca';
import DatePickerField from '../common/DatePickerField';
import { format, parse } from 'date-fns';

interface RomaneioFormData {
  id?: string;
  idFuncionario: number | null;
  idPraca: number | null;
  dataSaida: string;
  dataRetorno: string;
  finalizado: boolean;
  produtos: ProdutoRomaneio[];
}

const RomaneioForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<RomaneioFormData>({
    idFuncionario: null,
    idPraca: null,
    dataSaida: '',
    dataRetorno: '',
    finalizado: true,
    produtos: [],
  });

  // Converte yyyy-mm-dd para dd/mm/yyyy
  const convertToDisplayFormat = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = parse(dateString, 'yyyy-MM-dd', new Date());
    return format(date, 'dd/MM/yyyy');
  };

  // Converte dd/mm/yyyy para yyyy-mm-dd
  const convertToApiFormat = (dateString: string): string => {
    if (!dateString) return '';
    const date = parse(dateString, 'dd/MM/yyyy', new Date());
    return format(date, 'yyyy-MM-dd');
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [pracas, setPracas] = useState<Praca[]>([]);
  const [produtosDisponiveis, setProdutosDisponiveis] = useState<Produto[]>([]);
  const [produtoValues, setProdutoValues] = useState<Record<string, string>>({});
  const [produtoInputs, setProdutoInputs] = useState<{[key: number]: string}>({});

  // Buscar dados do romaneio para edição
  const { data: romaneioData, isLoading: romaneioLoading } = useQuery({
    queryKey: ['romaneio', id],
    queryFn: () => romaneioService.getById(id!),
    enabled: isEditing && !!id,
  });

  // Buscar funcionários
  const { data: funcionariosData } = useQuery({
    queryKey: ['funcionarios'],
    queryFn: () => funcionarioService.getAll(),
  });

  // Buscar produtos
  const { data: produtosData } = useQuery({
    queryKey: ['produtos'],
    queryFn: () => produtoService.getAll(),
  });

  // Buscar praças filtradas por funcionário
  const { data: pracasData } = useQuery({
    queryKey: ['pracas', formData.idFuncionario],
    queryFn: () => formData.idFuncionario ? pracaService.getByIdFuncionario(formData.idFuncionario) : pracaService.getAll(),
    enabled: !!formData.idFuncionario,
  });

  // Carregar funcionários
  useEffect(() => {
    if (funcionariosData) {
      setFuncionarios(funcionariosData);
    }
  }, [funcionariosData]);

  // Carregar praças
  useEffect(() => {
    if (pracasData) {
      setPracas(pracasData);
    }
  }, [pracasData]);

  // Carregar produtos disponíveis
  useEffect(() => {
    if (produtosData) {
      setProdutosDisponiveis(produtosData);
    }
  }, [produtosData]);

  // Carregar dados do romaneio quando estiver editando
  useEffect(() => {
    if (isEditing && romaneioData) {
      setFormData({
        id: romaneioData.id,
        idFuncionario: romaneioData.idFuncionario ? parseInt(romaneioData.idFuncionario) : null,
        idPraca: romaneioData.idPraca ? parseInt(romaneioData.idPraca) : null,
        dataSaida: romaneioData.dataSaida ? convertToDisplayFormat(romaneioData.dataSaida.split('T')[0]) : '',
        dataRetorno: romaneioData.dataRetorno ? convertToDisplayFormat(romaneioData.dataRetorno.split('T')[0]) : '',
        finalizado: romaneioData.finalizado || false,
        produtos: romaneioData.produtos || [],
      });
    }
  }, [isEditing, romaneioData]);

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: RomaneioFormData) => {
      const romaneioToSave: any = {
        idFuncionario: data.idFuncionario?.toString() || '',
        idPraca: data.idPraca?.toString() || '',
        dataSaida: convertToApiFormat(data.dataSaida),
        dataRetorno: convertToApiFormat(data.dataRetorno),
        finalizado: data.finalizado,
        produtos: data.produtos.map(produto => ({
          id: produto.id || '0',
          idRomaneio: data.id || '0',
          CodProduto: produto.idProduto || '',
          quantidade: parseInt(produto.quantidade.toString()) || 0,
          nomeProduto: produto.nomeProduto || '',
          precoVenda: produto.precoVenda || '0.00',
          referencia: produto.referencia || '',
          quantidadeDisponivel: produto.quantidadeDisponivel || 0,
          codigoBarras: produto.codigoBarras || [],
          idSacola: produto.idSacola || null,
        })) as any,
      };
      
      // Only include id when editing
      if (isEditing && data.id) {
        romaneioToSave.id = data.id;
      }
      
      console.log('Objeto sendo salvo (principal):', romaneioToSave);
      return romaneioService.salvar(romaneioToSave);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['romaneios'] });
      queryClient.refetchQueries({ queryKey: ['produtos'] });
      queryClient.refetchQueries({ queryKey: ['produtos', 'select'] });
      navigate('/romaneio');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar romaneio:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Erro ao salvar romaneio' 
      });
    },
  });

  const handleInputChange = (field: keyof RomaneioFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Limpar erro do campo quando usuário digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Funções para manipular produtos
  const addProduto = () => {
    setFormData(prev => ({
      ...prev,
      produtos: [...prev.produtos, {
        id: '0',
        idRomaneio: formData.id || '',
        idProduto: '0',
        quantidade: 1,
        precoVenda: '0',
        valorTotal: 0,
        nomeProduto: '',
        referencia: '',
        quantidadeDisponivel: 0,
      }]
    }));
  };

  const removeProduto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.filter((_, i) => i !== index)
    }));
  };

  const updateProduto = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const newProdutos = [...prev.produtos];
      const finalValue = field === 'precoVenda' ? String(value) : value;
      newProdutos[index] = { ...newProdutos[index], [field]: finalValue };
      
      // Calcular valor total se alterar quantidade ou valor unitário
      if (field === 'quantidade' || field === 'precoVenda') {
        const quantidade = Number(newProdutos[index].quantidade) || 0;
        const valorUnitario = Number(newProdutos[index].precoVenda) || 0;
        newProdutos[index].valorTotal = quantidade * valorUnitario;
      }
      
      return { ...prev, produtos: newProdutos };
    });
  };

  // Handler para input de valor unitário com vírgula
  const handlePrecoVendaChange = (index: number, value: string) => {
    const formattedValue = formatCurrencyInput(value);
    const numericValue = parseCurrencyToNumber(formattedValue);
    
    // Atualizar estado dos valores formatados
    setProdutoValues(prev => ({
      ...prev,
      [`precoVenda_${index}`]: formattedValue
    }));
    
    // Atualizar o produto com o valor numérico
    updateProduto(index, 'precoVenda', numericValue);
  };

  // Selecionar produto
  const handleProdutoChange = (index: number, produtoSelecionado: Produto | null) => {
    if (produtoSelecionado) {
      setFormData(prev => {
        const newProdutos = [...prev.produtos];
        newProdutos[index] = {
          ...newProdutos[index],
          idProduto: produtoSelecionado.id.toString(),
          nomeProduto: produtoSelecionado.nomeProduto,
          referencia: produtoSelecionado.referencia,
          precoVenda: produtoSelecionado.precoVenda.toString(),
          quantidadeDisponivel: produtoSelecionado.quantidadeDisponível || 0,
        };
        
        // Calcular valor total inicial
        const quantidade = Number(newProdutos[index].quantidade) || 0;
        const valorUnitario = Number(newProdutos[index].precoVenda) || 0;
        newProdutos[index].valorTotal = quantidade * valorUnitario;
        
        return { ...prev, produtos: newProdutos };
      });
      setProdutoInputs(prev => ({
        ...prev,
        [index]: produtoSelecionado.referencia || '',
      }));
    } else {
      setFormData(prev => {
        const newProdutos = [...prev.produtos];
        newProdutos[index] = {
          ...newProdutos[index],
          idProduto: '',
          nomeProduto: '',
          referencia: '',
          precoVenda: '0.00',
          quantidadeDisponivel: 0,
        };
        return { ...prev, produtos: newProdutos };
      });
      setProdutoInputs(prev => ({
        ...prev,
        [index]: '',
      }));
    }
  };

  const handleProdutoInputChange = (index: number, value: string) => {
    setProdutoInputs(prev => ({
      ...prev,
      [index]: value,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.idFuncionario) {
      newErrors.idFuncionario = 'Funcionário é obrigatório';
    }

    if (!formData.idPraca) {
      newErrors.idPraca = 'Praça é obrigatória';
    }

    if (!formData.dataSaida) {
      newErrors.dataSaida = 'Data de saída é obrigatória';
    }

    if (!formData.dataRetorno) {
      newErrors.dataRetorno = 'Data de retorno é obrigatória';
    }

    if (formData.produtos.length === 0) {
      newErrors.produtos = 'Selecione pelo menos um produto';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      saveMutation.mutate(formData);
    }
  };

  const handleBack = () => {
    navigate('/romaneio');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Função para formatar valor monetário para input (permite digitar com vírgula)
  const formatCurrencyInput = (value: string): string => {
    // Remove tudo que não é número ou vírgula
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    // Garante que só tenha uma vírgula
    const parts = cleanValue.split(',');
    if (parts.length > 2) {
      return parts[0] + ',' + parts.slice(1).join('');
    }
    
    return cleanValue;
  };

  // Função para converter string com vírgula para número
  const parseCurrencyToNumber = (value: string): number => {
    if (!value) return 0;
    // Substitui vírgula por ponto para conversão
    const numericValue = value.replace(',', '.');
    return parseFloat(numericValue) || 0;
  };

  // Função para formatar valor para exibição (com 2 casas decimais)
  const formatValueDisplay = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (romaneioLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEditing ? `Editar Romaneio #${id}` : 'Novo Romaneio'}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* Funcionário */}
            <Box  sx={{ flex: '1 1 45%', minWidth: 280 }}>
              <FormControl fullWidth error={!!errors.idFuncionario}>
                <InputLabel>Funcionário</InputLabel>
                <Select
                  value={formData.idFuncionario?.toString() || ''}
                  label="Funcionário"
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('idFuncionario', value ? parseInt(value) as any : null);
                  }}
                  disabled={isEditing}
                >
                  {funcionarios.map((func) => (
                    <MenuItem key={func.id} value={func.id}>
                      {func.nome}
                    </MenuItem>
                  ))}
                </Select>
                {errors.idFuncionario && <Typography variant="caption" color="error">{errors.idFuncionario}</Typography>}
              </FormControl>
            </Box>

            {/* Praça */}
            <Box  sx={{ flex: '1 1 45%', minWidth: 280 }}>
              <FormControl fullWidth error={!!errors.idPraca}>
                <InputLabel>Praça</InputLabel>
                <Select
                  value={formData.idPraca?.toString() || ''}
                  label="Praça"
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('idPraca', value ? parseInt(value) as any : null);
                  }}
                  disabled={isEditing || !formData.idFuncionario}
                >
                  {pracas.map((praca) => (
                    <MenuItem key={praca.id} value={praca.id}>
                      {praca.nomePraca}
                    </MenuItem>
                  ))}
                </Select>
                {errors.idPraca && <Typography variant="caption" color="error">{errors.idPraca}</Typography>}
              </FormControl>
            </Box>

            {/* Data de Saída */}
            <Box  sx={{ flex: '1 1 45%', minWidth: 280 }}>
              <DatePickerField
                label="Data de Saída"
                value={formData.dataSaida}
                onChange={(value) => handleInputChange('dataSaida', value)}
                error={!!errors.dataSaida}
                helperText={errors.dataSaida}
              />
            </Box>

            {/* Data de Retorno */}
            <Box  sx={{ flex: '1 1 45%', minWidth: 280 }}>
              <DatePickerField
                label="Data de Retorno"
                value={formData.dataRetorno}
                onChange={(value) => handleInputChange('dataRetorno', value)}
                error={!!errors.dataRetorno}
                helperText={errors.dataRetorno}
              />
            </Box>

            {/* Status */}
            <Box  sx={{ flex: '1 1 100%' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.finalizado}
                    onChange={(e) => handleInputChange('finalizado', e.target.checked)}
                  />
                }
                label={`Status: ${formData.finalizado ? 'FINALIZADO' : 'INICIADO'}`}
              />
            </Box>

            {/* Produtos */}
            <Box  sx={{ flex: '1 1 100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Produtos
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addProduto}
                >
                  Adicionar Produto
                </Button>
              </Box>

              {errors.produtos && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.produtos}
                </Alert>
              )}

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Valor Unitário</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.produtos.map((produto, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={(() => {
                              const input = (produtoInputs[index] ?? produto.referencia ?? '').toString().trim().toLowerCase();
                              if (!input) return produtosDisponiveis;
                              return produtosDisponiveis.filter((o) => {
                                const ref = (o.referencia || '').toString().toLowerCase();
                                const nome = (o.nomeProduto || '').toLowerCase();
                                return ref === input || nome.startsWith(input);
                              });
                            })()}
                            getOptionLabel={(option: Produto) => `${option.referencia || ''} - ${option.nomeProduto}`}
                            value={produto.idProduto ? produtosDisponiveis.find(p => p.id === Number(produto.idProduto)) || null : null}
                            getOptionKey={(option: Produto) => option.id}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            filterOptions={(options) => [...options]}
                            onChange={(_, newValue: Produto | null) => handleProdutoChange(index, newValue)}
                            renderInput={(params) => {
                              const digitado = produtoInputs[index]?.toString().trim();
                              const selected = produto.idProduto ? produtosDisponiveis.find(p => p.id === Number(produto.idProduto)) : null;
                              const inputValue = digitado
                                ? produtoInputs[index].toString()
                                : (selected ? `${selected.referencia || ''} - ${selected.nomeProduto || ''}` : produto.referencia || '');
                              return (
                                <TextField
                                  {...params}
                                  slotProps={{
                                    ...params.slotProps,
                                    htmlInput: {
                                      ...(params.slotProps?.htmlInput || {}),
                                      value: inputValue,
                                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleProdutoInputChange(index, e.target.value),
                                    },
                                  }}
                                  size="small"
                                  placeholder="Selecione um produto..."
                                />
                              );
                            }}
                          />
                        </TableCell>
                        <TableCell>{produto.referencia}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={produto.quantidade}
                            onChange={(e) => updateProduto(index, 'quantidade', Number(e.target.value))}
                            sx={{ width: 100 }}
                            slotProps={{
                              htmlInput: { min: 1 }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="text"
                            value={produtoValues[`precoVenda_${index}`] || formatValueDisplay(Number(produto.precoVenda))}
                            onChange={(e) => handlePrecoVendaChange(index, e.target.value)}
                            sx={{ width: 120 }}
                            slotProps={{
                              input: {
                                startAdornment: 'R$ '
                              }
                            }}
                            placeholder="0,00"
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(produto.valorTotal || 0)}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeProduto(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {formData.produtos.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography sx={{ py: 4, color: 'text.secondary' }}>
                            Nenhum produto adicionado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Erro geral */}
            {errors.submit && (
              <Box sx={{ flex: '1 1 100%' }}>
                <Alert severity="error">
                  {errors.submit}
                </Alert>
              </Box>
            )}

            {/* Total e Ações */}
            <Box sx={{ flex: '1 1 100%', mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Total: {formatCurrency(formData.produtos.reduce((total, produto) => total + (produto.valorTotal || 0), 0))}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={saveMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  startIcon={<SaveIcon />}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </Paper>


      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
};

export default RomaneioForm;
